import React, {useState, useEffect} from 'react';
import './App.css';

interface TimeFormulas {
    minutes: (value: number) => number;
    hours: (value: number) => number;
    days: (value: number) => number;
    weeks: (value: number) => number;
}

interface Friend {
    name: string;
    formulas: TimeFormulas;
}

interface TimeInputs {
    minutes: number;
    hours: number;
    days: number;
    weeks: number;
}

function App() {
    const friends: Friend[] = [
        {
            name: 'Jasiu',
            formulas: {
                minutes: (value) => Math.max(0, 1.5 * value),
                hours: (value) => Math.max(0, 2 * value / 1.5),
                days: (value) => Math.max(0, 2 * value - 1),
                // weeks: (value) => 1.5 * value,
                // weeks: (value) => Math.max(0, value <= 12 ? value : 12 * Math.exp(value - 12)),
                weeks: (value) => Math.max(0, value * (1 + Math.exp(2 * (value - 12)/16))),
            }
        }
    ];

    const [selectedFriend, setSelectedFriend] = useState<string>(friends[0].name);
    const [timeInputs, setTimeInputs] = useState<TimeInputs>({
        minutes: 0,
        hours: 0,
        days: 0,
        weeks: 0
    });
    const [calculatedTime, setCalculatedTime] = useState<string>('');
    const [isCountingDown, setIsCountingDown] = useState<boolean>(false);
    const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
    const [totalSeconds, setTotalSeconds] = useState<number>(0);
    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const [endTimestamp, setEndTimestamp] = useState<number>(0);
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const [isProcessingArrival, setIsProcessingArrival] = useState<boolean>(false);

    // Advanced settings
    const [notJasiu, setNotJasiu] = useState<boolean>(false);
    const [wasDrinking, setWasDrinking] = useState<boolean>(true);
    const [jasiuInStatusCall, setJasiuInStatusCall] = useState<boolean>(false);
    const [itsEvening, setItsEvening] = useState<boolean>(false);
    const [yesterdayAtAunts, setYesterdayAtAunts] = useState<boolean>(false);
    const [uncleArrived, setUncleArrived] = useState<boolean>(false);

    const funnyQuotes = [
        '🕳️ Somewhere past the event horizon — Jasiu-time cannot be observed from here.',
        '🐢 Slower than evolution, faster than Jasiu\'s last promise.',
        '🔮 A mythical era foretold only by ancient prophets (estimated arrival: maybe).',
        '🧊 Frozen in carbonite until further notice.',
        '📼 Scheduled for release in "Coming Soon™"… which means never.',
        '⏳ Lost in the sands of time — archaeologists might discover it first.',
        '🛸 Abducted by aliens; return date unknown, if ever.',
        '🦕 Expected sometime after the dinosaurs respawn.',
        '🛠️ In maintenance mode: completion ETA set to "whenever Jasiu feels like it."',
        '🌌 Located in a parallel universe where deadlines are purely decorative.',
        '⏰ Jasiu\'s clock runs on a timezone that doesn\'t exist yet.',
        '📅 Marked on a calendar from a dimension where months have 47 days.',
        '⌛ The hourglass broke, the sand turned to concrete, and we\'re still waiting.',
        '🕰️ Set your watch to "Jasiu Standard Time" — nobody knows what time that is.',
        '⏲️ The timer expired, respawned, and expired again. Still no ETA.',
        '🌅 Sometime between "eventually" and "when pigs develop flight schedules."',
        '📆 Penciled in for a date that only exists in leap centuries.',
        '⏱️ Measured in units science hasn\'t invented yet (possibly "Jasiu-seconds").',
        '🕐 Expected at o\'clock... but which one and in which millennium remains unclear.',
        '⏳ The countdown started, went backwards, and is now stuck in a time loop.'
    ];

    const getRandomQuote = (): string => {
        // Use current hour as seed to keep quote stable for an hour
        const hourOfDay = new Date().getHours();
        const index = hourOfDay % funnyQuotes.length;
        return funnyQuotes[index];
    };

    const encodeSettings = (): string => {
        // Encode settings as bit flags
        let flags = 0;
        if (notJasiu) flags |= 1;
        if (jasiuInStatusCall) flags |= 2;
        if (itsEvening) flags |= 4;
        if (yesterdayAtAunts) flags |= 8;
        if (uncleArrived) flags |= 16;
        // Convert to base36 for shorter string
        return flags.toString(36);
    };

    const decodeSettings = (encoded: string) => {
        try {
            const flags = parseInt(encoded, 36);
            setNotJasiu((flags & 1) !== 0);
            setJasiuInStatusCall((flags & 2) !== 0);
            setItsEvening((flags & 4) !== 0);
            setYesterdayAtAunts((flags & 8) !== 0);
            setUncleArrived((flags & 16) !== 0);
        } catch (e) {
            console.error('Failed to decode settings:', e);
        }
    };

    const calculateRealTime = () => {
        const friend = friends.find(f => f.name === selectedFriend);
        if (!friend) return;

        // Apply formulas or passthrough if "Not Jasiu" is enabled
        const realMinutes = notJasiu ? timeInputs.minutes : friend.formulas.minutes(timeInputs.minutes);
        const realHours = notJasiu ? timeInputs.hours : friend.formulas.hours(timeInputs.hours);
        const realDays = notJasiu ? timeInputs.days : friend.formulas.days(timeInputs.days);
        const realWeeks = notJasiu ? timeInputs.weeks : friend.formulas.weeks(timeInputs.weeks);

        // Convert everything to minutes for total calculation
        let totalMinutes =
            realMinutes +
            (realHours * 60) +
            (realDays * 24 * 60) +
            (realWeeks * 7 * 24 * 60);

        // Apply advanced settings modifiers (only if not "Not Jasiu")
        if (!notJasiu) {
            if (jasiuInStatusCall) totalMinutes += 1.5 * 60; // Add 1.5 hours
            if (itsEvening) totalMinutes += 20 * 60; // Add 20 hours
            if (yesterdayAtAunts) totalMinutes += 1 * 60; // Add 1 hour
            if (uncleArrived) totalMinutes += 2 * 24 * 60; // Add 2 days
        }

        console.log(totalMinutes)
        if (totalMinutes === 0) {
            setCalculatedTime('Enter a time interval to see the real duration');
            return;
        }

        const unknownInMinutes = 240 * 7 * 24 * 60;
        if (totalMinutes > unknownInMinutes) {
            setCalculatedTime(`When Jasiu says that time, it actually means:\n${getRandomQuote()}`);
            return;
        }

        // Convert back to human-readable format
        const weeks = Math.floor(totalMinutes / (7 * 24 * 60));
        const remainingAfterWeeks = totalMinutes % (7 * 24 * 60);
        const days = Math.floor(remainingAfterWeeks / (24 * 60));
        const remainingAfterDays = remainingAfterWeeks % (24 * 60);
        const hours = Math.floor(remainingAfterDays / 60);
        const minutes = Math.round(remainingAfterDays % 60);

        const parts: string[] = [];
        if (weeks >= 1) parts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
        if (days >= 1) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours >= 1) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes >= 1) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

        // Handle case where total is less than a minute but greater than 0
        if (parts.length === 0 && totalMinutes > 0) {
            const displayMinutes = Math.round(totalMinutes * 10) / 10;
            parts.push(`${displayMinutes} minute${displayMinutes !== 1 ? 's' : ''}`);
        }

        const result = parts.length > 0
            ? `When ${selectedFriend} says that time, it actually means:\n${parts.join(', ')}`
            : 'Enter a time interval to see the real duration';

        setCalculatedTime(result);
    };

    useEffect(() => {
        calculateRealTime();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFriend, timeInputs, notJasiu, jasiuInStatusCall, itsEvening, yesterdayAtAunts, uncleArrived]);

    useEffect(() => {
        // "Was drinking" randomly turns back on after 1-3 seconds
        if (!wasDrinking && !notJasiu) {
            const delay = Math.random() * 2000 + 1000; // 1-3 seconds
            const timer = setTimeout(() => {
                setWasDrinking(true);
            }, delay);
            return () => clearTimeout(timer);
        }
    }, [wasDrinking, notJasiu]);

    const resetSettings = () => {
        setNotJasiu(false);
        setWasDrinking(true);
        setJasiuInStatusCall(false);
        setItsEvening(false);
        setYesterdayAtAunts(false);
        setUncleArrived(false);
    };

    useEffect(() => {
        // Check for shared countdown link on mount
        const urlParams = new URLSearchParams(window.location.search);
        const startTime = urlParams.get('s');
        const endTime = urlParams.get('e');
        const settingsCode = urlParams.get('c');

        if (startTime && endTime) {
            const startTimestamp = parseInt(startTime, 36); // Decode from base36
            const endTimestampValue = parseInt(endTime, 36);
            const now = Math.floor(Date.now() / 1000);
            const remaining = endTimestampValue - now;
            const total = endTimestampValue - startTimestamp;

            // Decode and apply advanced settings from URL
            if (settingsCode) {
                decodeSettings(settingsCode);
            }

            if (remaining > 0 && total > 0) {
                setEndTimestamp(endTimestampValue);
                setTotalSeconds(total);
                setRemainingSeconds(remaining);
                setIsCountingDown(true);
            } else if (remaining <= 0 && total > 0) {
                // Countdown already finished
                setEndTimestamp(endTimestampValue);
                setTotalSeconds(total);
                setRemainingSeconds(0);
                setIsCompleted(true);
            }

            // Clean up URL parameters after consuming them
            const cleanUrl = `${window.location.origin}${window.location.pathname}`;
            window.history.replaceState({}, document.title, cleanUrl);
        }
    }, []);

    useEffect(() => {
        if (!isCountingDown || endTimestamp === 0) return;

        const timer = setInterval(() => {
            const now = Math.floor(Date.now() / 1000);
            const remaining = endTimestamp - now;

            if (remaining <= 0) {
                setRemainingSeconds(0);
                setIsCountingDown(false);
                setIsCompleted(true);
            } else {
                setRemainingSeconds(remaining);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [isCountingDown, endTimestamp]);

    const startCountdown = () => {
        const friend = friends.find(f => f.name === selectedFriend);
        if (!friend) return;

        // Apply formulas or passthrough if "Not Jasiu" is enabled
        const realMinutes = notJasiu ? timeInputs.minutes : friend.formulas.minutes(timeInputs.minutes);
        const realHours = notJasiu ? timeInputs.hours : friend.formulas.hours(timeInputs.hours);
        const realDays = notJasiu ? timeInputs.days : friend.formulas.days(timeInputs.days);
        const realWeeks = notJasiu ? timeInputs.weeks : friend.formulas.weeks(timeInputs.weeks);

        let totalMinutes = 
            realMinutes +
            (realHours * 60) +
            (realDays * 24 * 60) +
            (realWeeks * 7 * 24 * 60);

        // Apply advanced settings modifiers (only if not "Not Jasiu")
        if (!notJasiu) {
            if (jasiuInStatusCall) totalMinutes += 1.5 * 60; // Add 1.5 hours
            if (itsEvening) totalMinutes += 20 * 60; // Add 20 hours
            if (yesterdayAtAunts) totalMinutes += 1 * 60; // Add 1 hour
            if (uncleArrived) totalMinutes += 2 * 24 * 60; // Add 2 days
        }

        const unknownInMinutes = 240 * 7 * 24 * 60;
        if (totalMinutes <= 0 || totalMinutes > unknownInMinutes) return;

        const seconds = Math.round(totalMinutes * 60);
        const now = Math.floor(Date.now() / 1000);
        const endTime = now + seconds;

        setEndTimestamp(endTime);
        setTotalSeconds(seconds);
        setRemainingSeconds(seconds);
        setIsCountingDown(true);
    };

    const stopCountdown = () => {
        setIsCountingDown(false);
        setRemainingSeconds(0);
        setIsCompleted(false);
        setEndTimestamp(0);
    };

    const handleHeGotHere = () => {
        setIsProcessingArrival(true);
        setTimeout(() => {
            setIsProcessingArrival(false);
            stopCountdown();
        }, 2000);
    };

    const formatCountdown = (seconds: number): string => {
        const weeks = Math.floor(seconds / (7 * 24 * 60 * 60));
        const days = Math.floor((seconds % (7 * 24 * 60 * 60)) / (24 * 60 * 60));
        const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
        const mins = Math.floor((seconds % (60 * 60)) / 60);
        const secs = seconds % 60;

        const parts: string[] = [];
        if (weeks > 0) parts.push(`${weeks}w`);
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0) parts.push(`${mins}m`);
        if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

        return parts.join(' ');
    };

    const getProgressPercentage = (): number => {
        if (totalSeconds === 0) return 0;
        return ((totalSeconds - remainingSeconds) / totalSeconds) * 100;
    };

    const shareCountdown = async () => {
        const now = Math.floor(Date.now() / 1000);
        const endTimestamp = now + remainingSeconds;
        const startTimestamp = now + remainingSeconds - totalSeconds;

        // Encode settings as compact string
        const settingsCode = encodeSettings();

        const params = new URLSearchParams({
            s: startTimestamp.toString(36), // Use base36 for shorter numbers
            e: endTimestamp.toString(36),
            c: settingsCode
        });

        const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;

        const shareData = {
            title: 'Jasiu Time Countdown',
            text: `Join me in waiting for Jasiu! ⏰`,
            url: shareUrl
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(shareUrl);
                alert('Link copied to clipboard! 📋');
            }
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const timeUnitMaxValues = {
        minutes: 60,
        hours: 24,
        days: 31,
        weeks: 52
    };

    const handleSliderChange = (unit: keyof TimeInputs, value: string) => {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0) {
            setTimeInputs(prev => ({
                ...prev,
                [unit]: numValue
            }));
        }
    };

    return (
        <div className={`App ${uncleArrived && (isCountingDown || isCompleted) ? 'drunk-mode' : ''}`}>
            <div className="container">
                <button 
                    className={`settings-icon-button ${showSettings ? 'active' : ''}`}
                    onClick={() => setShowSettings(!showSettings)}
                    title="Advanced Settings"
                >
                    S
                </button>

                <h1>Jasiu Time Calculator</h1>
                <p className="subtitle">Calculate how much time Jasiu really means</p>

                <div className="content-wrapper">
                {/*<div className="friend-selector">*/}
                {/*  <label htmlFor="friend-select">Select Friend:</label>*/}
                {/*  <select*/}
                {/*    id="friend-select"*/}
                {/*    value={selectedFriend}*/}
                {/*    onChange={(e) => setSelectedFriend(e.target.value)}*/}
                {/*  >*/}
                {/*    {friends.map(friend => (*/}
                {/*      <option key={friend.name} value={friend.name}>*/}
                {/*        {friend.name}*/}
                {/*      </option>*/}
                {/*    ))}*/}
                {/*  </select>*/}
                {/*</div>*/}

                                      {showSettings ? (
                  <div className="settings-panel">
                    <h2>⚙️ Advanced Settings</h2>
                    <div className="settings-scroll">
                      <button className="reset-button" onClick={resetSettings}>
                        Reset to Defaults
                      </button>

                      <div className="setting-item">
                        <label>
                          <input
                            type="checkbox"
                            checked={notJasiu}
                            onChange={(e) => {
                              setNotJasiu(e.target.checked);
                              if (e.target.checked) {
                                setWasDrinking(false);
                                setJasiuInStatusCall(false);
                                setItsEvening(false);
                                setYesterdayAtAunts(false);
                                setUncleArrived(false);
                              }
                            }}
                          />
                          <span className="setting-label">Not Jasiu</span>
                        </label>
                      </div>

                      <div className={`setting-item ${notJasiu ? 'disabled' : ''}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={wasDrinking && !notJasiu}
                            onChange={(e) => setWasDrinking(e.target.checked)}
                            disabled={notJasiu}
                          />
                          <span className="setting-label">Jasiu was drinking</span>
                        </label>
                      </div>

                      <div className={`setting-item ${notJasiu ? 'disabled' : ''}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={jasiuInStatusCall && !notJasiu}
                            onChange={(e) => setJasiuInStatusCall(e.target.checked)}
                            disabled={notJasiu}
                          />
                          <span className="setting-label">Jasiu in status call</span>
                        </label>
                      </div>

                      <div className={`setting-item ${notJasiu ? 'disabled' : ''}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={itsEvening && !notJasiu}
                            onChange={(e) => setItsEvening(e.target.checked)}
                            disabled={notJasiu}
                          />
                          <span className="setting-label">It's evening</span>
                        </label>
                      </div>

                      <div className={`setting-item ${notJasiu ? 'disabled' : ''}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={yesterdayAtAunts && !notJasiu}
                            onChange={(e) => setYesterdayAtAunts(e.target.checked)}
                            disabled={notJasiu}
                          />
                          <span className="setting-label">Yesterday Jasiu was at aunt's</span>
                        </label>
                      </div>

                      <div className={`setting-item ${notJasiu ? 'disabled' : ''}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={uncleArrived && !notJasiu}
                            onChange={(e) => setUncleArrived(e.target.checked)}
                            disabled={notJasiu}
                          />
                          <span className="setting-label">Uncle arrived</span>
                        </label>
                      </div>
                    </div>

                    <button 
                      className="action-button start-button" 
                      onClick={() => setShowSettings(false)}
                      style={{ marginTop: '20px' }}
                    >
                      Back to Calculator
                    </button>
                  </div>
                                      ) : !isCountingDown && !isCompleted ? (
                  <>
                    <div className="time-inputs">
                      <h2>Enter Time Interval</h2>
                      <div className="slider-container">
                        {Object.keys(timeInputs).map((unit) => (
                          <div key={unit} className="slider-group">
                            <div className="slider-header">
                              <label htmlFor={unit}>{unit.charAt(0).toUpperCase() + unit.slice(1)}</label>
                              <span className="slider-value">{timeInputs[unit as keyof TimeInputs]}</span>
                            </div>
                            <input
                              id={unit}
                              type="range"
                              min="0"
                              max={timeUnitMaxValues[unit as keyof TimeInputs]}
                              step="1"
                              value={timeInputs[unit as keyof TimeInputs]}
                              onChange={(e) => handleSliderChange(unit as keyof TimeInputs, e.target.value)}
                              className="slider"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="result">
                      <h2>Real Time Duration</h2>
                      <p className="result-text">{calculatedTime}</p>
                    </div>

                    <button 
                      className="action-button start-button" 
                      onClick={startCountdown}
                      disabled={
                        calculatedTime.includes('Enter a time interval') || 
                        funnyQuotes.some(quote => calculatedTime.includes(quote.substring(0, 10)))
                      }
                    >
                      Let's wait! ⏰
                    </button>
                  </>
                ) : (
                  <>
                    <div className="countdown-display">
                      {isProcessingArrival && (
                        <div className="loading-overlay">
                          <div className="loading-spinner"></div>
                          <p className="loading-text">Totally recording this data... </p>
                          <p className="loading-text">We will surely use this important information to not improve the accuracy of the calculator</p>
                        </div>
                      )}
                      <h2>{isCompleted ? '🎉 Time Complete!' : '⏳ Countdown in Progress'}</h2>
                      <div className="countdown-timer">
                        {formatCountdown(remainingSeconds)}
                      </div>
                      <div className="progress-bar-container">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${getProgressPercentage()}%` }}
                        ></div>
                      </div>
                      <p className="countdown-info">
                        {isCompleted 
                          ? (notJasiu ? "Time's up! 🎊" : "Jasiu's time has passed! You waited it out! 🎊")
                          : (notJasiu 
                              ? "Countdown in progress..." 
                              : (yesterdayAtAunts 
                                  ? "Probably he is in the toilet right now 🚽"
                                  : `Waiting for Jasiu's time...`))}
                      </p>
                    </div>

                    <div className="button-group">
                      {!isCompleted && (
                        <>
                          <button 
                            className="action-button share-button" 
                            onClick={shareCountdown}
                          >
                            Share Timer
                          </button>
                          <button 
                            className="action-button arrived-button" 
                            onClick={handleHeGotHere}
                            title="We will totally use this important information to not improve the accuracy of the calculator"
                          >
                            He got here!
                          </button>
                        </>
                      )}
                      <button 
                        className="action-button stop-button" 
                        onClick={stopCountdown}
                        disabled={isProcessingArrival}
                      >
                        {isCompleted ? 'Go Back' : 'Stop & Reset'}
                      </button>
                    </div>
                  </>
                )}
                </div>
            </div>
        </div>
    );
}

export default App;
